import { useEffect, useRef, useState, type FormEvent } from "react";
import { createPortal } from "react-dom";
import { Icon } from "@/components/ui/Icon";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/useToast";
import { useBackdropDismiss } from "@/hooks/useBackdropDismiss";
import { AuthError, changeMyPassword } from "@/services/auth.service";
import { runAccountOp } from "@/services/account_ops.service";
import "./account.css";

/**
 * PasswordChangeModal — 비밀번호 변경(D-1 · 12 설계 §8-D · §13 ⑬ · §14 Q9).
 *
 * 평면 폼이라 베이스 `.modal`(사이드탭 전용) 대신 `.modal-backdrop > .modal-panel`(DESIGN 상황별 패턴 "평면 폼 모달").
 * 순서: 현재 비밀번호로 재인증 → 새 비밀번호 → account_ops self.password_changed(이 기기 빼고 다른 기기 즉시 로그아웃).
 * 모달 안 안내 상자에 "다른 기기 n대 로그아웃"을 미리 보여 준다(사용자 결정).
 */
const MIN_LEN = 6;

const ERR: Partial<Record<string, string>> = {
  WRONG_CURRENT_PASSWORD: "현재 비밀번호가 맞지 않습니다.",
  WEAK_PASSWORD: `새 비밀번호가 너무 약합니다. ${MIN_LEN}자 이상으로 입력하세요.`,
  TOO_MANY_ATTEMPTS: "시도가 너무 많습니다. 잠시 후 다시 시도하세요.",
  REQUIRES_RECENT_LOGIN: "보안을 위해 다시 로그인한 뒤 바꿔 주세요.",
};

export function PasswordChangeModal({ otherDevices, onClose }: { otherDevices: number; onClose: () => void }) {
  const { deviceId } = useAuth();
  const toast = useToast();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const firstRef = useRef<HTMLInputElement>(null);
  const dismiss = useBackdropDismiss(onClose, { disabled: busy });

  useEffect(() => {
    firstRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !busy) {
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [busy, onClose]);

  function validate(): string {
    if (!current) return "현재 비밀번호를 입력하세요.";
    if (next.length < MIN_LEN) return `새 비밀번호는 ${MIN_LEN}자 이상이어야 합니다.`;
    if (next === current) return "현재 비밀번호와 다른 비밀번호를 입력하세요.";
    if (next !== confirmPw) return "새 비밀번호 두 칸이 일치하지 않습니다.";
    return "";
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const v = validate();
    if (v) {
      setErr(v);
      return;
    }
    setErr("");
    setBusy(true);
    try {
      await changeMyPassword(current, next);
    } catch (e2) {
      const code = e2 instanceof AuthError ? e2.code : "UNKNOWN";
      setErr(ERR[code] ?? `비밀번호를 바꾸지 못했습니다: ${(e2 as Error).message}`);
      setBusy(false);
      return;
    }
    // 비밀번호는 바뀜 — 이제 다른 기기 정리(실패해도 변경은 유효, Firebase 가 최대 1시간 안에 끊음)
    let revoked = otherDevices;
    try {
      const res = await runAccountOp<{ flagged?: number }>("self.password_changed", { keep_device_id: deviceId });
      if (typeof res?.flagged === "number") revoked = res.flagged;
      toast.success(
        revoked > 0 ? `비밀번호를 바꿨습니다. 다른 기기 ${revoked}대는 로그아웃됐습니다.` : "비밀번호를 바꿨습니다.",
      );
    } catch (e3) {
      toast.success("비밀번호를 바꿨습니다.");
      if (otherDevices > 0) {
        toast.info(
          `다른 기기 로그아웃 요청은 실패했습니다(${(e3 as Error).message}). 다른 기기는 1시간 안에 자동으로 로그아웃됩니다.`,
          8000,
        );
      }
    }
    setBusy(false);
    onClose();
  }

  return createPortal(
    <div className="modal-backdrop ma-pw-backdrop" {...dismiss}>
      <form
        className="modal-panel ma-pw-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ma-pw-title"
        onSubmit={onSubmit}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-head">
          <h3 id="ma-pw-title">비밀번호 변경</h3>
          <button type="button" className="modal-close" onClick={onClose} aria-label="닫기" disabled={busy}>
            <Icon name="close" size={18} />
          </button>
        </div>

        <div className="ma-note is-warn" role="note">
          <Icon name="warning" size={16} />
          <span>
            {otherDevices > 0 ? (
              <>
                변경하면 이 기기를 뺀 <b>다른 기기({otherDevices}대)는 모두 로그아웃</b>됩니다. 다른 기기에서는 새 비밀번호로
                다시 로그인하세요.
              </>
            ) : (
              <>변경하면 다른 기기에서는 새 비밀번호로 다시 로그인해야 합니다. 지금 등록된 다른 기기는 없습니다.</>
            )}
          </span>
        </div>

        <label className="field">
          <span>현재 비밀번호</span>
          <input
            ref={firstRef}
            type="password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            autoComplete="current-password"
            disabled={busy}
          />
        </label>
        <label className="field">
          <span>새 비밀번호</span>
          <input
            type="password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            autoComplete="new-password"
            placeholder={`${MIN_LEN}자 이상`}
            disabled={busy}
          />
        </label>
        <label className="field">
          <span>새 비밀번호 확인</span>
          <input
            type="password"
            value={confirmPw}
            onChange={(e) => setConfirmPw(e.target.value)}
            autoComplete="new-password"
            disabled={busy}
          />
        </label>
        <div className="ma-form-err" role="alert">{err}</div>

        <div className="form-actions ma-pw-actions">
          <button type="button" className="btn-ghost" onClick={onClose} disabled={busy}>
            취소
          </button>
          <button type="submit" className="btn-primary" disabled={busy}>
            {busy ? "바꾸는 중…" : "비밀번호 변경"}
          </button>
        </div>
      </form>
    </div>,
    document.body,
  );
}
